/**
 * Meal Planning Agent
 * Creates weekly meal plans using local, seasonal produce
 */

interface MealPlan {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  days: DayPlan[];
  shoppingList: ShoppingListItem[];
  nutritionSummary: NutritionSummary;
  estimatedCost: number;
  localPercentage: number;
}

interface DayPlan {
  date: Date;
  meals: {
    breakfast: Meal | null;
    lunch: Meal | null;
    dinner: Meal | null;
    snacks: Meal[];
  };
  nutritionTotal: DailyNutrition;
}

interface Meal {
  id: string;
  name: string;
  type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  ingredients: Ingredient[];
  prepTime: number;
  cookTime: number;
  servings: number;
  nutrition: MealNutrition;
  localIngredients: string[];
  recipe?: string;
  tags: string[];
}

interface Ingredient {
  name: string;
  quantity: number;
  unit: string;
  isLocal: boolean;
  isSeasonal: boolean;
  category: string;
}

interface MealNutrition {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

interface DailyNutrition extends MealNutrition {
  targetMet: boolean;
}

interface NutritionSummary {
  averageCalories: number;
  averageProtein: number;
  averageCarbs: number;
  averageFat: number;
  daysOnTarget: number;
}

interface ShoppingListItem {
  name: string;
  quantity: number;
  unit: string;
  category: string;
  isLocal: boolean;
  suggestedFarm?: string;
}

interface UserPreferences {
  dietaryRestrictions: string[];
  dislikedIngredients: string[];
  favoriteIngredients: string[];
  cookingSkill: 'beginner' | 'intermediate' | 'advanced';
  maxPrepTime: number; // minutes per meal
  budget: 'low' | 'medium' | 'high';
  mealPrepPreference: 'fresh-daily' | 'batch-cooking' | 'mixed';
  householdSize: number;
}

class MealPlanningAgent {
  private mealTemplates: Meal[] = [
    {
      id: 'farm-eggs-toast',
      name: 'Farm Fresh Eggs with Toast',
      type: 'breakfast',
      ingredients: [
        { name: 'eggs', quantity: 2, unit: 'each', isLocal: true, isSeasonal: true, category: 'protein' },
        { name: 'bread', quantity: 2, unit: 'slices', isLocal: true, isSeasonal: true, category: 'grains' },
        { name: 'butter', quantity: 1, unit: 'tbsp', isLocal: true, isSeasonal: true, category: 'dairy' },
      ],
      prepTime: 5,
      cookTime: 10,
      servings: 1,
      nutrition: { calories: 350, protein: 18, carbs: 28, fat: 20, fiber: 2 },
      localIngredients: ['eggs', 'bread'],
      tags: ['quick', 'protein-rich', 'classic'],
    },
    {
      id: 'seasonal-salad',
      name: 'Seasonal Garden Salad',
      type: 'lunch',
      ingredients: [
        { name: 'mixed greens', quantity: 4, unit: 'cups', isLocal: true, isSeasonal: true, category: 'vegetables' },
        { name: 'tomatoes', quantity: 1, unit: 'cup', isLocal: true, isSeasonal: true, category: 'vegetables' },
        { name: 'cucumber', quantity: 0.5, unit: 'each', isLocal: true, isSeasonal: true, category: 'vegetables' },
        { name: 'olive oil', quantity: 2, unit: 'tbsp', isLocal: false, isSeasonal: true, category: 'oils' },
      ],
      prepTime: 15,
      cookTime: 0,
      servings: 2,
      nutrition: { calories: 180, protein: 4, carbs: 12, fat: 14, fiber: 4 },
      localIngredients: ['mixed greens', 'tomatoes', 'cucumber'],
      tags: ['fresh', 'light', 'seasonal'],
    },
    {
      id: 'grilled-chicken-vegetables',
      name: 'Grilled Chicken with Farm Vegetables',
      type: 'dinner',
      ingredients: [
        { name: 'chicken breast', quantity: 6, unit: 'oz', isLocal: true, isSeasonal: true, category: 'protein' },
        { name: 'zucchini', quantity: 1, unit: 'each', isLocal: true, isSeasonal: true, category: 'vegetables' },
        { name: 'bell peppers', quantity: 1, unit: 'each', isLocal: true, isSeasonal: true, category: 'vegetables' },
        { name: 'herbs', quantity: 2, unit: 'tbsp', isLocal: true, isSeasonal: true, category: 'herbs' },
      ],
      prepTime: 15,
      cookTime: 25,
      servings: 2,
      nutrition: { calories: 380, protein: 42, carbs: 15, fat: 16, fiber: 4 },
      localIngredients: ['chicken', 'zucchini', 'bell peppers', 'herbs'],
      tags: ['protein-rich', 'healthy', 'grilled'],
    },
    {
      id: 'veggie-stir-fry',
      name: 'Local Vegetable Stir Fry',
      type: 'dinner',
      ingredients: [
        { name: 'mixed vegetables', quantity: 4, unit: 'cups', isLocal: true, isSeasonal: true, category: 'vegetables' },
        { name: 'tofu', quantity: 8, unit: 'oz', isLocal: false, isSeasonal: true, category: 'protein' },
        { name: 'rice', quantity: 1, unit: 'cup', isLocal: false, isSeasonal: true, category: 'grains' },
        { name: 'soy sauce', quantity: 2, unit: 'tbsp', isLocal: false, isSeasonal: true, category: 'condiments' },
      ],
      prepTime: 20,
      cookTime: 15,
      servings: 2,
      nutrition: { calories: 420, protein: 22, carbs: 52, fat: 14, fiber: 6 },
      localIngredients: ['mixed vegetables'],
      tags: ['vegetarian', 'quick', 'healthy'],
    },
    {
      id: 'smoothie-bowl',
      name: 'Tropical Smoothie Bowl',
      type: 'breakfast',
      ingredients: [
        { name: 'frozen berries', quantity: 1, unit: 'cup', isLocal: true, isSeasonal: true, category: 'fruit' },
        { name: 'banana', quantity: 1, unit: 'each', isLocal: false, isSeasonal: true, category: 'fruit' },
        { name: 'yogurt', quantity: 0.5, unit: 'cup', isLocal: true, isSeasonal: true, category: 'dairy' },
        { name: 'granola', quantity: 0.25, unit: 'cup', isLocal: true, isSeasonal: true, category: 'grains' },
      ],
      prepTime: 10,
      cookTime: 0,
      servings: 1,
      nutrition: { calories: 380, protein: 12, carbs: 68, fat: 8, fiber: 8 },
      localIngredients: ['berries', 'yogurt', 'granola'],
      tags: ['quick', 'refreshing', 'no-cook'],
    },
  ];

  /**
   * Generate a weekly meal plan
   */
  generateWeeklyPlan(
    preferences: UserPreferences,
    startDate: Date = new Date()
  ): MealPlan {
    const days: DayPlan[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      const dayPlan = this.planDay(preferences, date, i);
      days.push(dayPlan);
    }

    const shoppingList = this.generateShoppingList(days, preferences.householdSize);
    const nutritionSummary = this.calculateNutritionSummary(days);
    const estimatedCost = this.estimateCost(shoppingList, preferences.budget);
    const localPercentage = this.calculateLocalPercentage(shoppingList);

    return {
      id: `plan-${Date.now()}`,
      name: `Week of ${startDate.toLocaleDateString()}`,
      startDate,
      endDate: new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000),
      days,
      shoppingList,
      nutritionSummary,
      estimatedCost,
      localPercentage,
    };
  }

  /**
   * Plan a single day
   */
  private planDay(preferences: UserPreferences, date: Date, dayIndex: number): DayPlan {
    const breakfast = this.selectMeal('breakfast', preferences);
    const lunch = this.selectMeal('lunch', preferences);
    const dinner = this.selectMeal('dinner', preferences);

    const allMeals = [breakfast, lunch, dinner].filter(Boolean) as Meal[];
    const nutritionTotal = this.calculateDailyNutrition(allMeals);

    return {
      date,
      meals: {
        breakfast,
        lunch,
        dinner,
        snacks: [],
      },
      nutritionTotal,
    };
  }

  /**
   * Select a meal based on preferences
   */
  private selectMeal(type: Meal['type'], preferences: UserPreferences): Meal | null {
    const candidates = this.mealTemplates.filter(meal => {
      if (meal.type !== type) return false;

      // Check dietary restrictions
      if (preferences.dietaryRestrictions.includes('vegetarian')) {
        if (meal.ingredients.some(i => ['chicken', 'beef', 'pork', 'fish'].includes(i.name.toLowerCase()))) {
          return false;
        }
      }

      // Check prep time
      if (meal.prepTime + meal.cookTime > preferences.maxPrepTime) {
        return false;
      }

      // Check disliked ingredients
      if (meal.ingredients.some(i =>
        preferences.dislikedIngredients.some(d =>
          i.name.toLowerCase().includes(d.toLowerCase())
        )
      )) {
        return false;
      }

      return true;
    });

    if (candidates.length === 0) return null;

    // Score and select best candidate
    const scored = candidates.map(meal => ({
      meal,
      score: this.scoreMeal(meal, preferences),
    }));

    scored.sort((a, b) => b.score - a.score);

    // Add some randomness
    const topCandidates = scored.slice(0, Math.min(3, scored.length));
    return topCandidates[Math.floor(Math.random() * topCandidates.length)].meal;
  }

  /**
   * Score a meal based on preferences
   */
  private scoreMeal(meal: Meal, preferences: UserPreferences): number {
    let score = 50;

    // Bonus for local ingredients
    score += meal.localIngredients.length * 5;

    // Bonus for favorite ingredients
    meal.ingredients.forEach(i => {
      if (preferences.favoriteIngredients.some(f =>
        i.name.toLowerCase().includes(f.toLowerCase())
      )) {
        score += 10;
      }
    });

    // Bonus for quick meals if batch cooking preferred
    if (preferences.mealPrepPreference === 'batch-cooking' && meal.prepTime < 15) {
      score += 10;
    }

    return score;
  }

  /**
   * Calculate daily nutrition
   */
  private calculateDailyNutrition(meals: Meal[]): DailyNutrition {
    const totals = meals.reduce(
      (acc, meal) => ({
        calories: acc.calories + meal.nutrition.calories,
        protein: acc.protein + meal.nutrition.protein,
        carbs: acc.carbs + meal.nutrition.carbs,
        fat: acc.fat + meal.nutrition.fat,
        fiber: acc.fiber + meal.nutrition.fiber,
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
    );

    // Check if daily targets are met (using 2000 cal as baseline)
    const targetMet = totals.calories >= 1800 && totals.calories <= 2200 && totals.protein >= 50;

    return { ...totals, targetMet };
  }

  /**
   * Generate shopping list from meal plan
   */
  private generateShoppingList(days: DayPlan[], householdSize: number): ShoppingListItem[] {
    const itemMap = new Map<string, ShoppingListItem>();

    days.forEach(day => {
      const meals = [day.meals.breakfast, day.meals.lunch, day.meals.dinner].filter(Boolean) as Meal[];

      meals.forEach(meal => {
        meal.ingredients.forEach(ingredient => {
          const key = ingredient.name.toLowerCase();
          const existing = itemMap.get(key);

          if (existing) {
            existing.quantity += ingredient.quantity * householdSize;
          } else {
            itemMap.set(key, {
              name: ingredient.name,
              quantity: ingredient.quantity * householdSize,
              unit: ingredient.unit,
              category: ingredient.category,
              isLocal: ingredient.isLocal,
            });
          }
        });
      });
    });

    return Array.from(itemMap.values()).sort((a, b) => a.category.localeCompare(b.category));
  }

  /**
   * Calculate nutrition summary
   */
  private calculateNutritionSummary(days: DayPlan[]): NutritionSummary {
    const totals = days.reduce(
      (acc, day) => ({
        calories: acc.calories + day.nutritionTotal.calories,
        protein: acc.protein + day.nutritionTotal.protein,
        carbs: acc.carbs + day.nutritionTotal.carbs,
        fat: acc.fat + day.nutritionTotal.fat,
        daysOnTarget: acc.daysOnTarget + (day.nutritionTotal.targetMet ? 1 : 0),
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0, daysOnTarget: 0 }
    );

    return {
      averageCalories: Math.round(totals.calories / days.length),
      averageProtein: Math.round(totals.protein / days.length),
      averageCarbs: Math.round(totals.carbs / days.length),
      averageFat: Math.round(totals.fat / days.length),
      daysOnTarget: totals.daysOnTarget,
    };
  }

  /**
   * Estimate cost
   */
  private estimateCost(items: ShoppingListItem[], budget: string): number {
    const priceMultiplier = budget === 'low' ? 0.8 : budget === 'high' ? 1.3 : 1;

    return items.reduce((total, item) => {
      const basePrice = item.isLocal ? 4 : 3; // Local premium
      return total + item.quantity * basePrice * priceMultiplier;
    }, 0);
  }

  /**
   * Calculate local percentage
   */
  private calculateLocalPercentage(items: ShoppingListItem[]): number {
    const localItems = items.filter(i => i.isLocal).length;
    return Math.round((localItems / items.length) * 100);
  }

  /**
   * Get meal suggestions for a specific meal type
   */
  getMealSuggestions(type: Meal['type']): Meal[] {
    return this.mealTemplates.filter(m => m.type === type);
  }

  /**
   * Swap a meal in the plan
   */
  swapMeal(plan: MealPlan, dayIndex: number, mealType: 'breakfast' | 'lunch' | 'dinner', newMeal: Meal): MealPlan {
    const updatedDays = [...plan.days];
    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      meals: {
        ...updatedDays[dayIndex].meals,
        [mealType]: newMeal,
      },
    };

    // Recalculate nutrition for the day
    const meals = [
      updatedDays[dayIndex].meals.breakfast,
      updatedDays[dayIndex].meals.lunch,
      updatedDays[dayIndex].meals.dinner,
    ].filter(Boolean) as Meal[];

    updatedDays[dayIndex].nutritionTotal = this.calculateDailyNutrition(meals);

    return {
      ...plan,
      days: updatedDays,
      shoppingList: this.generateShoppingList(updatedDays, 2), // Assuming household size 2
      nutritionSummary: this.calculateNutritionSummary(updatedDays),
    };
  }
}

const mealPlanningAgent = new MealPlanningAgent();
export default mealPlanningAgent;
